import React, { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Search, MapPin, Star, Users, MessageSquare, Zap, ChevronDown, Shield, Lock, CalendarCheck, Bookmark, Plane } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import ReliabilityBadge, { VerifiedPartnerBadge } from "@/components/ReliabilityBadge";
import SkillConfidenceBadge from "@/components/SkillConfidenceBadge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SPORTS = ["tennis", "pickleball", "golf", "table-tennis", "ski", "padel", "squash", "badminton", "climbing"];
const SKILLS = ["beginner", "intermediate", "advanced", "expert"];
const SPORT_LABELS = {
  "tennis": "Tennis", "pickleball": "Pickleball", "golf": "Golf",
  "table-tennis": "Table Tennis", "ski": "Skiing", "padel": "Padel",
  "squash": "Squash", "badminton": "Badminton", "climbing": "Climbing"
};

// Sport-specific rating systems
const SPORT_RATING = {
  tennis: { label: "NTRP", format: v => v },
  pickleball: { label: "DUPR", format: v => v },
  golf: { label: "HCP", format: v => v },
  "table-tennis": { label: "USATT", format: v => v },
  ski: { label: "", format: v => v },
  padel: { label: "Lvl", format: v => v },
  squash: { label: "PSA", format: v => v },
  badminton: { label: "BWF", format: v => v },
  climbing: { label: "", format: v => `V${v}` },
};

function SkillBadge({ level }) {
  const classes = {
    beginner: "badge-beginner", intermediate: "badge-intermediate",
    advanced: "badge-advanced", expert: "badge-expert"
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${classes[level] || "badge-intermediate"}`}>
      {level}
    </span>
  );
}

function PartnerCard({ partner, onMessage, selectedSport, signedIn }) {
  const sports = partner.sports || [];
  const primarySport = sports[0];
  const initials = partner.name?.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  const confKey = selectedSport || primarySport?.sport;
  const confidence = (partner.skill_confidence && (partner.skill_confidence[confKey] || partner.skill_confidence[primarySport?.sport])) || null;

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:border-brand-green/30 transition-all duration-200 group" data-testid="partner-card">
      {/* Avatar section */}
      <div className="relative p-5 pb-3">
        <div className="flex items-start gap-4">
          <div className="relative flex-shrink-0">
            {partner.avatar_url ? (
              <img src={partner.avatar_url} alt={partner.name} className="w-14 h-14 rounded-xl object-cover" />
            ) : (
              <div className="w-14 h-14 rounded-xl bg-brand-green/10 flex items-center justify-center text-brand-green font-black font-heading text-lg">
                {initials}
              </div>
            )}
            {partner.subscription === "pro" && (
              <div className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-brand-green rounded-full flex items-center justify-center">
                <Zap size={10} className="text-white" fill="currentColor" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-brand-navy text-base truncate">{partner.name}</h3>
            <div className="flex items-center gap-1 text-gray-600 text-xs mt-0.5">
              <MapPin size={11} />
              <span className="truncate">{partner.city || "Fort Worth, TX"}</span>
            </div>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              {partner.verified_partner && <VerifiedPartnerBadge size="xs" />}
              {confidence && <SkillConfidenceBadge level={confidence.level} label={confidence.label} size="xs" />}
              {partner.reliability_badge && (
                <ReliabilityBadge badge={partner.reliability_badge.badge} label={partner.reliability_badge.label} size="xs" />
              )}
              <span className="text-xs text-gray-600">{partner.completed_sessions || 0} sessions</span>
              {partner.available_this_week && (
                <span className="inline-flex items-center gap-0.5 text-xs text-green-400 font-medium">
                  <CalendarCheck size={10} /> This week
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Sports list */}
        <div className="mt-3 flex flex-wrap gap-1.5">
          {sports.slice(0, 3).map((s, i) => {
            const ratingCfg = SPORT_RATING[s.sport];
            const ratingDisplay = s.rating != null && ratingCfg
              ? `${ratingCfg.label ? ratingCfg.label + " " : ""}${ratingCfg.format(s.rating)}`
              : null;
            return (
              <div key={i} className="flex items-center gap-1">
                <span className="text-xs text-gray-600 capitalize">{SPORT_LABELS[s.sport] || s.sport}</span>
                <SkillBadge level={s.skill_level} />
                {ratingDisplay && (
                  <span className="text-xs text-gray-500 font-mono">{ratingDisplay}</span>
                )}
                {s.skill_verified && (
                  <Shield size={10} className="text-brand-green flex-shrink-0" aria-label="Skill verified" title="Skill Verified" />
                )}
              </div>
            );
          })}
        </div>

        {/* Bio */}
        {partner.bio && (
          <p className="mt-2 text-xs text-gray-500 leading-relaxed">{partner.bio}</p>
        )}

        {/* Languages */}
        {partner.languages?.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {partner.languages.map((lang) => (
              <span key={lang} className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded">{lang}</span>
            ))}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="px-5 pb-4 flex gap-2 border-t border-gray-200 pt-3 mt-1">
        <Link
          to={`/profile/${partner.id}`}
          className="flex-1 text-center py-2 text-sm text-gray-600 border border-gray-200 rounded-xl hover:border-brand-green/40 hover:text-brand-green transition-colors"
          data-testid="view-profile-button"
        >
          View Profile
        </Link>
        <button
          onClick={() => onMessage(partner)}
          className="flex-1 flex items-center justify-center gap-1.5 py-2 text-sm font-bold bg-brand-orange text-white rounded-xl hover:bg-brand-orange-hover transition-colors"
          data-testid="message-partner-button"
        >
          <MessageSquare size={14} /> {signedIn ? "Message" : "Sign up to message"}
        </button>
      </div>
    </div>
  );
}

export default function FindPartnersPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const [partners, setPartners] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isPro, setIsPro] = useState(false);

  // Pre-fill city from travel mode if active
  const travelActive = user?.travel_enabled && user?.travel_city;
  const defaultCity = searchParams.get("city") || (travelActive ? user.travel_city : "");

  const [filters, setFilters] = useState({
    sport: searchParams.get("sport") || "",
    city: defaultCity,
    skill_level: searchParams.get("skill") || "",
    women_only: false,
    available_this_week: false,
    min_rating: "",
    max_rating: "",
    language: "",
  });
  const [page, setPage] = useState(1);

  const fetchPartners = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.sport) params.set("sport", filters.sport);
      if (filters.city) params.set("city", filters.city);
      if (filters.skill_level) params.set("skill_level", filters.skill_level);
      if (filters.women_only) params.set("women_only", "true");
      if (filters.available_this_week) params.set("available_this_week", "true");
      if (filters.min_rating !== "") params.set("min_rating", filters.min_rating);
      if (filters.max_rating !== "") params.set("max_rating", filters.max_rating);
      if (filters.language) params.set("language", filters.language);
      params.set("page", page);
      params.set("limit", 12);
      const { data } = await axios.get(`${API}/partners/search?${params}`, { withCredentials: true });
      setPartners(data.partners || []);
      setTotal(data.total || 0);
      setIsPro(data.is_pro || false);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => { fetchPartners(); }, [fetchPartners]);

  const handleMessage = async (partner) => {
    // Preserve the search so a visitor who signs up lands back on the results
    // they were looking at, rather than on an empty Find Partners page. The
    // previous bare redirect to /login dropped every active filter.
    if (!user) {
      const back = encodeURIComponent(`/find-partners?${searchParams.toString()}`);
      window.location.href = `/register?next=${back}`;
      return;
    }
    try {
      await axios.post(`${API}/conversations`, { participant_id: partner.id, message: `Hi ${partner.name}! I'd love to connect for a ${filters.sport || "sports"} session.` });
      window.location.href = "/messages";
    } catch (e) {
      console.error(e);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      {/* Travel Mode Banner */}
      {travelActive && (
        <div className="bg-brand-green/5 border-b border-brand-green/20 py-2.5 px-4" role="status" data-testid="travel-mode-banner">
          <div className="max-w-7xl mx-auto flex items-center gap-2 text-sm">
            <Plane size={14} className="text-brand-green flex-shrink-0" aria-hidden="true" />
            <span className="text-brand-green font-medium">Travel Mode Active</span>
            <span className="text-gray-600">
              — showing players in <strong className="text-brand-navy font-semibold">{user.travel_city}</strong>
              {user.travel_start && user.travel_end && ` · ${user.travel_start} → ${user.travel_end}`}
            </span>
            <Link to="/account" className="ml-auto text-xs text-slate-400 hover:text-brand-green underline flex-shrink-0">Edit</Link>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-white border-b border-gray-100 py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl font-heading font-black text-brand-navy mb-1">Find Play Partners</h1>
          <p className="text-gray-500 text-sm">
            {total > 0 ? `${total} players found` : "Search for players by sport, city, and skill level"}
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Search bar */}
        <div className="bg-white border border-gray-200 rounded-xl p-4 mb-6 shadow-sm" data-testid="search-filters">
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Sport */}
            <div className="relative flex-1">
              <select
                value={filters.sport}
                onChange={(e) => handleFilterChange("sport", e.target.value)}
                className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-brand-navy text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-brand-green/40 focus:border-brand-green cursor-pointer"
                aria-label="Filter partners by sport"
                data-testid="sport-filter"
              >
                <option value="">All Sports</option>
                {SPORTS.map((s) => (
                  <option key={s} value={s}>{SPORT_LABELS[s]}</option>
                ))}
              </select>
              <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 pointer-events-none" />
            </div>

            {/* City */}
            <div className="relative flex-1">
              <MapPin size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                type="text"
                value={filters.city}
                onChange={(e) => handleFilterChange("city", e.target.value)}
                placeholder="City (e.g. Fort Worth)"
                className="w-full bg-gray-50 border border-gray-200 rounded-xl pl-9 pr-4 py-2.5 text-brand-navy text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-green/40 focus:border-brand-green"
                aria-label="Filter by city"
                data-testid="city-filter"
              />
            </div>

            {/* Skill level */}
            <div className="relative flex-1">
              <select
                value={filters.skill_level}
                onChange={(e) => handleFilterChange("skill_level", e.target.value)}
                className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-brand-navy text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-brand-green/40 focus:border-brand-green cursor-pointer"
                aria-label="Filter by skill level"
                data-testid="skill-filter"
              >
                <option value="">All Skill Levels</option>
                {SKILLS.map((s) => (
                  <option key={s} value={s} className="capitalize">{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                ))}
              </select>
              <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 pointer-events-none" />
            </div>

            <button
              onClick={fetchPartners}
              className="flex items-center gap-2 px-5 py-2.5 bg-brand-orange text-white font-bold rounded-xl hover:bg-brand-orange-hover transition-colors whitespace-nowrap"
              data-testid="search-partners-button"
            >
              <Search size={16} />
              Search
            </button>
          </div>

          {/* Advanced Filters */}
          <div className="mt-3 pt-3 border-t border-gray-200 space-y-3">
            <span className="text-xs text-gray-500">Advanced Filters:</span>
            <div className="flex flex-wrap gap-3">
              {/* Available this week toggle */}
              <button
                onClick={() => handleFilterChange("available_this_week", !filters.available_this_week)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                  filters.available_this_week
                    ? "bg-green-500/10 border-green-400/40 text-green-600"
                    : "border-gray-200 text-gray-500 hover:border-brand-green/40"
                }`}
                data-testid="available-this-week-toggle"
              >
                <CalendarCheck size={11} /> Available This Week
              </button>
              {/* Women-only toggle — shown only to female users */}
              {user?.gender === "female" && (
                isPro || user?.subscription === "pro" || user?.subscription === "elite" ? (
                  <button
                    onClick={() => handleFilterChange("women_only", !filters.women_only)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                      filters.women_only
                        ? "bg-brand-green/10 border-brand-green text-brand-green"
                        : "border-gray-200 text-gray-600 hover:border-brand-green/30"
                    }`}
                    data-testid="women-only-filter"
                  >
                    <Shield size={11} aria-hidden="true" />
                    Women Only
                  </button>
                ) : (
                  <Link
                    to="/pricing"
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-gray-200 text-gray-500 rounded-xl hover:border-brand-green/20 transition-colors"
                    data-testid="women-only-upgrade-prompt"
                  >
                    <Lock size={11} aria-hidden="true" />
                    Women Only
                    <span className="text-brand-green font-bold ml-0.5">Pro</span>
                  </Link>
                )
              )}

              {/* Rating range — shown when sport is selected */}
              {filters.sport && (() => {
                const ratingCfg = SPORT_RATING[filters.sport];
                const label = ratingCfg?.label || "Rating";
                return (
                  <div className="flex items-center gap-1.5" data-testid="rating-range-filter">
                    <span className="text-xs text-gray-500 whitespace-nowrap">{label} Range:</span>
                    <input
                      type="number"
                      value={filters.min_rating}
                      onChange={e => handleFilterChange("min_rating", e.target.value)}
                      placeholder="Min"
                      className="w-16 bg-gray-50 border border-gray-200 rounded-lg px-2 py-1 text-brand-navy text-xs focus:outline-none focus:ring-1 focus:ring-brand-green/40"
                      aria-label="Minimum rating"
                data-testid="min-rating-input"
                    />
                    <span className="text-gray-600 text-xs">–</span>
                    <input
                      type="number"
                      value={filters.max_rating}
                      onChange={e => handleFilterChange("max_rating", e.target.value)}
                      placeholder="Max"
                      className="w-16 bg-gray-50 border border-gray-200 rounded-lg px-2 py-1 text-brand-navy text-xs focus:outline-none focus:ring-1 focus:ring-brand-green/40"
                      aria-label="Maximum rating"
                data-testid="max-rating-input"
                    />
                  </div>
                );
              })()}

              {/* Language filter */}
              <div className="flex items-center gap-1.5">
                <select
                  value={filters.language}
                  onChange={e => handleFilterChange("language", e.target.value)}
                  className="bg-gray-50 border border-gray-200 rounded-lg px-2 py-1 text-xs text-brand-navy focus:outline-none"
                  aria-label="Preferred language"
                data-testid="language-filter"
                >
                  <option value="">Any Language</option>
                  {["English", "Spanish", "French", "Mandarin", "Korean", "Portuguese", "German", "Japanese"].map(l => (
                    <option key={l} value={l}>{l}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Results */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-brand-green border-t-transparent rounded-full animate-spin" />
          </div>
        ) : partners.length === 0 ? (
          <div className="text-center py-20">
            <Users size={48} className="mx-auto text-gray-600 mb-4" />
            <h3 className="text-lg font-bold text-brand-navy mb-2">No players found</h3>
            <p className="text-gray-500 text-sm">Try adjusting your filters or{" "}
              <Link to="/register" className="text-brand-green hover:underline">join to be the first in your area</Link>.
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="partners-grid">
              {partners.map((p) => (
                <PartnerCard key={p.id} partner={p} onMessage={handleMessage} selectedSport={filters.sport} signedIn={!!user} />
              ))}
            </div>

            {/* Pagination */}
            {total > 12 && (
              <div className="flex justify-center gap-2 mt-8">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-4 py-2 text-sm bg-white border border-gray-200 text-gray-600 rounded-xl hover:border-brand-green/40 disabled:opacity-40 transition-colors"
                >
                  Previous
                </button>
                <span className="px-4 py-2 text-sm text-gray-600">
                  Page {page} of {Math.ceil(total / 12)}
                </span>
                <button
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page >= Math.ceil(total / 12)}
                  className="px-4 py-2 text-sm bg-white border border-gray-200 text-gray-600 rounded-xl hover:border-brand-green/40 disabled:opacity-40 transition-colors"
                >
                  Next
                </button>
              </div>
            )}

            {/* Free tier cap notice */}
            {!isPro && total > 12 && (
              <div className="flex items-center justify-center gap-2 mt-4 py-3 px-4 bg-brand-green/5 border border-brand-green/15 rounded-xl" data-testid="pro-upgrade-prompt">
                <Lock size={13} className="text-brand-green flex-shrink-0" aria-hidden="true" />
                <p className="text-sm text-slate-400">
                  {total - partners.length} more players available.{" "}
                  <Link to="/pricing" className="text-brand-green font-medium hover:underline">Upgrade to Pro</Link> to see all results.
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
